// Authentication utilities and types
import { Language } from '../contexts/LanguageContext';

export interface User {
  id: string;
  _id?: string; // MongoDB _id (optional, for compatibility)
  name: string;
  email: string;
  role: 'SystemAdmin' | 'OrganizationAdmin' | 'OrganizationUser';
  organizationId: string;
  organizationName?: string;
  avatar?: string;
  status?: 'Active' | 'Pending' | 'Inactive' | 'Suspended';
  createdAt?: string | Date;
  updatedAt?: string | Date;
  createdBy?: string;
  updatedBy?: string;
  isActive?: boolean;
  managedBusinessIds?: string[];
  theme?: 'light' | 'dark';
  language?: Language;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
  expiresAt: string;
}

export interface RegisterRequest {
  companyName: string;
  contactName: string;
  contactEmail: string;
  password: string;
  phoneNumber: string;
  termsAccepted: boolean;
  kvkkConsentGiven: boolean;
}

// FE-003: setToken()/getToken()'s storage-reading were vestigial after the cookie-auth
// migration -- the real access token lives only in the backend's httpOnly cookie, and nothing
// in the app ever called setToken() to populate localStorage/sessionStorage anymore (confirmed:
// the only call sites were this file's own now-deleted test). removeToken() is kept since it's
// still useful for clearing any leftover storage from before that migration on logout.
export const removeToken = (): void => {
  localStorage.removeItem('revomira_token');
  sessionStorage.removeItem('revomira_token');
};

/**
 * Reads the readable CSRF cookie set by the backend on login.
 * Sent back in the X-CSRF-Token header on mutating requests (double-submit).
 */
export const getCsrfToken = (): string | null => {
  const match = document.cookie.match(/(?:^|;\s*)revomira_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Non-secret flag marking that an agency user is currently acting as a client
 * organization. The tokens themselves live in the httpOnly cookie; the backend
 * swaps them on switch-context / restore-context. This flag only drives the
 * "return to agency" banner in the UI.
 */
export const ACTING_AS_CLIENT_KEY = 'revomira_acting_as_client';

export const setActingAsClient = (): void => {
  sessionStorage.setItem(ACTING_AS_CLIENT_KEY, '1');
};

export const isActingAsClient = (): boolean =>
  sessionStorage.getItem(ACTING_AS_CLIENT_KEY) === '1';

export const clearActingAsClient = (): void => {
  sessionStorage.removeItem(ACTING_AS_CLIENT_KEY);
};

export const getUser = (): User | null => {
  // Try localStorage first (remember me), then sessionStorage
  const userStr = localStorage.getItem('revomira_user') || sessionStorage.getItem('revomira_user');
  if (!userStr || userStr === 'undefined' || userStr === 'null') {
    return null;
  }
  try {
    return JSON.parse(userStr);
  } catch (_error) {
    // Invalid JSON, remove it
    localStorage.removeItem('revomira_user');
    sessionStorage.removeItem('revomira_user');
    return null;
  }
};

export const setUser = (user: User, rememberMe: boolean = false): void => {
  if (rememberMe) {
    localStorage.setItem('revomira_user', JSON.stringify(user));
    sessionStorage.removeItem('revomira_user');
  } else {
    sessionStorage.setItem('revomira_user', JSON.stringify(user));
    localStorage.removeItem('revomira_user');
  }
};

export const removeUser = (): void => {
  localStorage.removeItem('revomira_user');
  sessionStorage.removeItem('revomira_user');
};

// Role-based access control
export const hasRole = (user: User | null, roles: string[]): boolean => {
  if (!user) return false;
  return roles.includes(user.role);
};

export const isSystemAdmin = (user: User | null): boolean => {
  return hasRole(user, ['SystemAdmin']);
};

export const isOrganizationAdmin = (user: User | null): boolean => {
  return hasRole(user, ['SystemAdmin', 'OrganizationAdmin']);
};

export const canAccessOrganization = (user: User | null, organizationId: string): boolean => {
  if (!user) return false;
  if (isSystemAdmin(user)) return true;
  return user.organizationId === organizationId;
};
